import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert, Box, Button, Chip, Divider, IconButton, MenuItem, Paper, Snackbar,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  BudgetVersion, Commitment, CostDocument, CostLine, Paginated,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";
import { DecisionDialog } from "../components/DecisionDialog";
import { DecisionHistory } from "../components/DecisionHistory";
import { STAN_KOSZTU } from "../lib/costState";
import { formatMoney, parseMoney, sumMoney } from "../lib/money";
import { NewCommitmentDialog } from "./NewCommitmentDialog";

const PUSTA_LINIA: CostLine = {
  id: null, ordinal: 1, description: "", quantity: "1.000", unit: "",
  amount_net: "", vat_code: "23", is_aggregate: false, origin: "manual",
  allocations: [],
};

/** Różnica między kwotą a sumą części — dodatnia znaczy „brakuje". */
function brakuje(calosc: string, czesci: string[]): string {
  return sumMoney([calosc, `-${sumMoney(czesci)}`]);
}

export function CostDetailPage() {
  const { code = "", id = "" } = useParams();
  const { can } = useAuth();
  const kolejka = useQueryClient();
  const [linie, setLinie] = useState<CostLine[]>([]);
  const [blad, setBlad] = useState<ApiError | null>(null);
  const [komunikat, setKomunikat] = useState<string | null>(null);
  const [nowaUmowa, setNowaUmowa] = useState(false);
  const [decyzja, setDecyzja] = useState<"approve" | "reject" | null>(null);

  const { data: koszt } = useQuery({
    queryKey: ["cost", id],
    queryFn: () => api.get<CostDocument>(`/costs/${id}`),
  });
  const { data: wersje } = useQuery({
    queryKey: ["budgets", code],
    queryFn: () => api.get<Paginated<BudgetVersion>>(`/projects/${code}/budgets`),
  });
  const wersjaId = useMemo(() => {
    const lista = wersje?.results ?? [];
    return (lista.find((w) => w.status === "approved" || w.status === "locked")
            ?? lista[0])?.id;
  }, [wersje]);
  const { data: wersja } = useQuery({
    queryKey: ["budget", wersjaId],
    queryFn: () => api.get<BudgetVersion>(`/budgets/${wersjaId}`),
    enabled: Boolean(wersjaId),
  });
  const { data: umowy } = useQuery({
    queryKey: ["commitments", code],
    queryFn: () => api.get<Paginated<Commitment>>(`/projects/${code}/commitments`),
  });

  // Ten sam wzorzec co w edytorze kosztorysu: wczytujemy do stanu tylko przy
  // zmianie dokumentu albo jego stanu, żeby pobranie w tle nie skasowało
  // niezapisanego opisu.
  const wczytany = useRef("");
  useEffect(() => {
    if (!koszt) return;
    const znacznik = `${koszt.id}:${koszt.state}:${koszt.lines.length}`;
    if (wczytany.current === znacznik) return;
    wczytany.current = znacznik;
    setLinie(koszt.lines.map((l) => ({ ...PUSTA_LINIA, ...l })));
  }, [koszt]);

  const pozycjeKosztorysu = wersja?.lines ?? [];
  const listaUmow = umowy?.results ?? [];
  const edytowalny = can("cost.allocate")
    && !["accepted", "handed_over", "rejected"].includes(koszt?.state ?? "");

  const doRozdysponowania = brakuje(
    koszt?.amount_net_control ?? "0", linie.map((l) => l.amount_net || "0"),
  );

  function zmienLinie(i: number, zmiana: Partial<CostLine>) {
    setLinie((s) => s.map((l, k) => (k === i ? { ...l, ...zmiana } : l)));
  }

  function zmienAlokacje(li: number, ai: number, zmiana: Partial<CostLine["allocations"][0]>) {
    setLinie((s) => s.map((l, k) => k !== li ? l : {
      ...l, allocations: l.allocations.map((a, j) => (j === ai ? { ...a, ...zmiana } : a)),
    }));
  }

  const zapisz = useMutation({
    mutationFn: () =>
      api.put<CostDocument>(`/costs/${id}/lines`, {
        lines: linie.map((l, i) => ({
          ordinal: i + 1, description: l.description || "Pozycja",
          quantity: l.quantity || "1", unit: l.unit,
          amount_net: parseMoney(l.amount_net) || "0", vat_code: l.vat_code || "23",
          is_aggregate: l.is_aggregate, origin: l.origin,
          allocations: l.allocations
            .filter((a) => a.budget_line && a.commitment)
            .map((a) => ({
              budget_line: a.budget_line, commitment: a.commitment,
              amount_net: parseMoney(a.amount_net) || "0", note: a.note ?? "",
            })),
        })),
      }),
    onSuccess: (nowy) => {
      wczytany.current = "";
      kolejka.setQueryData(["cost", id], nowy);
      kolejka.invalidateQueries({ queryKey: ["costs", code] });
      setBlad(null);
      setKomunikat("Zapisano opis.");
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const przejscie = useMutation({
    mutationFn: ({ co, comment }: { co: string; comment?: string }) =>
      api.post<CostDocument>(`/costs/${id}/${co}`,
                             comment === undefined ? undefined : { comment }),
    onSuccess: (nowy) => {
      wczytany.current = "";
      kolejka.setQueryData(["cost", id], nowy);
      kolejka.invalidateQueries({ queryKey: ["costs", code] });
      kolejka.invalidateQueries({ queryKey: ["report", code] });
      setBlad(null);
      setDecyzja(null);
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  if (!koszt) return null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <BackButton fallback={`/projekty/${code}/koszty`} />
        <Typography variant="h6">
          {koszt.seller_name_snapshot || koszt.seller_nip} · {koszt.number}
        </Typography>
        <Chip size="small" label={STAN_KOSZTU[koszt.state]?.label ?? koszt.state}
              color={STAN_KOSZTU[koszt.state]?.color ?? "default"} />
        <Typography variant="body2" color="text.secondary">
          {koszt.issued_on} · netto {formatMoney(koszt.amount_net_control)} zł
          {koszt.source === "ksef" ? " · KSeF" : ""}
        </Typography>
        {koszt.file && (
          <Button size="small" href={koszt.file} target="_blank">Załącznik</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {edytowalny && (
          <>
            <Button startIcon={<AddIcon />}
                    onClick={() => setLinie((s) => [...s, { ...PUSTA_LINIA }])}>
              Pozycja
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />}
                    onClick={() => zapisz.mutate()} disabled={zapisz.isPending}>
              Zapisz opis
            </Button>
          </>
        )}
        {edytowalny && koszt.problems.length === 0 && koszt.state !== "described_full" && (
          <Button variant="outlined" onClick={() => przejscie.mutate({ co: "complete" })}>
            Oznacz jako kompletny
          </Button>
        )}
        {can("cost.accept") && koszt.state === "described_full" && (
          <>
            <Button color="error" onClick={() => setDecyzja("reject")}>
              Odeślij do poprawy
            </Button>
            <Button variant="contained" color="success"
                    onClick={() => setDecyzja("approve")}>
              Zaakceptuj
            </Button>
          </>
        )}
      </Stack>

      {koszt.amount_mismatch && Number(koszt.amount_mismatch) !== 0 && (
        <Alert severity="warning">
          Kwota z GT różni się od kontrolnej o {formatMoney(koszt.amount_mismatch)} zł.
          To literówka, dopasowanie do niewłaściwej faktury albo korekta u sprzedawcy —
          każdy z tych przypadków warto sprawdzić.
        </Alert>
      )}

      {blad && (
        <Alert severity="error" onClose={() => setBlad(null)}>
          {blad.message}
          {blad.body.problems.length > 0 && (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {blad.body.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
        </Alert>
      )}

      {koszt.decisions?.length ? <DecisionHistory decyzje={koszt.decisions} /> : null}

      {koszt.problems.length > 0 && (
        <Alert severity="info">
          Zanim koszt trafi do kolumny „Rzeczywiste”, musi się bilansować:
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {koszt.problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </Alert>
      )}

      {linie.map((linia, li) => {
        // Liczymy tylko przypisania kompletne: zapis i tak pomija te bez
        // wskazanej pozycji albo rodzica, więc wliczanie ich pokazywałoby
        // pozycję jako rozdysponowaną, a po zapisie okazywałaby się pusta.
        const reszta = brakuje(
          linia.amount_net || "0",
          linia.allocations
            .filter((a) => a.budget_line && a.commitment)
            .map((a) => a.amount_net || "0"),
        );
        const domkniete = Number(reszta) === 0;
        return (
          <Paper key={li} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label="Pozycja faktury" value={linia.description} sx={{ flex: 3 }}
                         disabled={!edytowalny}
                         onChange={(e) => zmienLinie(li, { description: e.target.value })} />
              <TextField label="Netto" value={linia.amount_net} sx={{ width: 130 }}
                         disabled={!edytowalny}
                         onChange={(e) => zmienLinie(li, { amount_net: e.target.value })} />
              <Chip size="small" color={domkniete ? "success" : "warning"}
                    label={domkniete ? "rozdysponowana"
                                     : `do rozdysponowania ${formatMoney(reszta)}`} />
              <Box sx={{ flex: 1 }} />
              {edytowalny && linie.length > 1 && (
                <IconButton onClick={() => setLinie((s) => s.filter((_, k) => k !== li))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={1}>
              {linia.allocations.map((alokacja, ai) => (
                <Stack key={ai} direction="row" spacing={2} alignItems="center">
                  <TextField select label="Pozycja kosztorysu" sx={{ flex: 3 }}
                             disabled={!edytowalny} value={alokacja.budget_line ?? ""}
                             onChange={(e) =>
                               zmienAlokacje(li, ai, { budget_line: e.target.value })}>
                    {pozycjeKosztorysu.map((p) => (
                      <MenuItem key={p.id ?? ""} value={p.id ?? ""}>
                        {p.account_code} — {p.description}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField select label="Rodzic (zaangażowanie)" sx={{ flex: 3 }}
                             disabled={!edytowalny} value={alokacja.commitment ?? ""}
                             helperText="Każdy koszt ma rodzica — także zakup drobny."
                             onChange={(e) =>
                               zmienAlokacje(li, ai, { commitment: e.target.value })}>
                    {listaUmow.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.title} · {u.counterparty_name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField label="Kwota" sx={{ width: 130 }} disabled={!edytowalny}
                             value={alokacja.amount_net}
                             onChange={(e) =>
                               zmienAlokacje(li, ai, { amount_net: e.target.value })} />

                  {edytowalny && (
                    <IconButton onClick={() => setLinie((s) => s.map((l, k) => k !== li ? l : {
                      ...l, allocations: l.allocations.filter((_, j) => j !== ai),
                    }))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}

              {edytowalny && (
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<AddIcon />}
                          onClick={() => setLinie((s) => s.map((l, k) => k !== li ? l : {
                            ...l,
                            allocations: [...l.allocations, {
                              budget_line: "", commitment: "",
                              // Podpowiadamy resztę: najczęstszy przypadek to
                              // jedna alokacja na całość pozycji.
                              amount_net: reszta, note: "",
                            }],
                          }))}>
                    Dodaj przypisanie
                  </Button>
                  <Button size="small" onClick={() => setNowaUmowa(true)}>
                    Nowe zaangażowanie
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        );
      })}

      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color={Number(doRozdysponowania) === 0
          ? "text.secondary" : "error.main"}>
          Suma pozycji {formatMoney(sumMoney(linie.map((l) => l.amount_net || "0")))} zł
          wobec kwoty kontrolnej {formatMoney(koszt.amount_net_control)} zł
          {Number(doRozdysponowania) !== 0
            && ` — różnica ${formatMoney(doRozdysponowania)} zł`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {edytowalny && (
          <Tooltip title="Dokument dotyczy kilku produkcji — trafi do księgowej
                          zamiast być przypisany na siłę do jednej.">
            <Button color="warning"
                    onClick={() => przejscie.mutate({ co: "multi-project" })}>
              Dotyczy kilku produkcji
            </Button>
          </Tooltip>
        )}
      </Stack>

      <DecisionDialog
        open={decyzja !== null}
        tryb={decyzja ?? "approve"}
        tytul={decyzja === "reject" ? "Odeślij opis do poprawy" : "Zaakceptuj koszt"}
        opis={decyzja === "reject"
          ? "Dokument wróci do opisania z Twoim uzasadnieniem."
          : "Koszt zostanie zaakceptowany do przekazania księgowości."}
        blad={blad?.field("comment")}
        pracuje={przejscie.isPending}
        onClose={() => { setDecyzja(null); setBlad(null); }}
        onConfirm={(comment) =>
          przejscie.mutate({ co: decyzja === "reject" ? "reject" : "accept", comment })}
      />

      <NewCommitmentDialog open={nowaUmowa} onClose={() => setNowaUmowa(false)}
                           projectCode={code} />
      <Snackbar open={Boolean(komunikat)} autoHideDuration={2500}
                onClose={() => setKomunikat(null)} message={komunikat} />
    </Stack>
  );
}
